import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, Blocks, Eye, EyeOff, LogIn } from "lucide-react";
import { useAuth } from "../../auth/context";
import Button from "../../components/ui/Button";
import Input, { Label } from "../../components/ui/Input";

const Login = () => {
  const { isAuthenticated, isTeam, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Sent here because the session expired: say so, and return to that page
  // afterwards. `next` only ever as a path on this site.
  const params = new URLSearchParams(location.search);
  const expired = params.get("expired") === "1";
  const next = params.get("next") || "";
  const safeNext = next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/login") ? next : "";

  // Where the user was headed before the gate bounced them here.
  const from = location.state?.from?.pathname || safeNext || "/";

  if (isAuthenticated && isTeam) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // The backend's Joi schema rejects anything shorter, with a message that
    // reads like a validation dump — catch it here instead.
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setBusy(true);
    const { error: err } = await login({ email: email.trim(), password });
    setBusy(false);

    if (err) {
      setError(err);
      return;
    }
    navigate(from, { replace: true });
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-ink-50 px-5 py-12">
      <div className="absolute inset-0 bg-mesh-1" aria-hidden />

      <div className="relative w-full max-w-sm anim-in">
        <div className="mb-7 flex flex-col items-center text-center">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-gradient shadow-brand-glow">
            <Blocks size={22} className="text-white" />
          </span>
          <h1 className="mt-4 text-h1 text-ink-900">Super admin</h1>
          <p className="mt-1.5 text-sm text-ink-500">
            Sign in to manage marketplace apps.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-2xl border border-ink-200/70 bg-white p-6 shadow-card-elevated"
        >
          {expired && !error && (
            <div
              role="status"
              className="mb-5 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3"
            >
              <AlertCircle size={15} className="mt-0.5 shrink-0 text-amber-600" />
              <p className="text-[13px] leading-relaxed text-amber-800">Your session expired. Sign in again to continue where you left off.</p>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-3"
            >
              <AlertCircle size={15} className="mt-0.5 shrink-0 text-rose-600" />
              <p className="text-[13px] leading-relaxed text-rose-800">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                required
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={revealed ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setRevealed((v) => !v)}
                  className="absolute right-1 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                  aria-label={revealed ? "Hide password" : "Show password"}
                >
                  {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            variant="gradient"
            size="lg"
            loading={busy}
            className="mt-6 w-full"
            icon={!busy && <LogIn size={15} />}
          >
            Sign in
          </Button>
        </form>

        <p className="mt-5 text-center text-xs leading-relaxed text-ink-400">
          Admin access only. Accounts are created from the backend.
        </p>
      </div>
    </div>
  );
};

export default Login;
